class PillToggle extends HTMLElement {
  connectedCallback() {
    this.pills = Array.from(this.querySelectorAll('.pill-toggle__pill'));
    this.panels = Array.from(this.querySelectorAll('.pill-toggle__panel'));

    // Optional: the sibling tab-panel image to swap when the active pill changes.
    // Absent unless this pill-toggle lives in a tab panel that has a media image.
    this.panelImage =
      this.closest('.tabbed-content__panel')?.querySelector('.tabbed-content__panel-image') || null;
    if (this.panelImage) {
      // Cache the authored default so an option with no image restores it.
      this.defaultImageSrc = this.panelImage.getAttribute('src') || '';
      this.defaultImageSrcset = this.panelImage.getAttribute('srcset') || '';
      // Warm the alternate option images so the first toggle doesn't flash.
      this.preloadOptionImages();
    }

    // Optional: a sibling CTA button whose href follows the active pill.
    // Only wired up when the block authors at least one per-option CTA link;
    // the target is the first <a> that follows this element among its
    // siblings (the button block placed right after the toggle).
    this.ctaLink = null;
    if (this.dataset.option1CtaLink || this.dataset.option2CtaLink || this.dataset.option3CtaLink) {
      let sibling = this.nextElementSibling;
      while (sibling && !this.ctaLink) {
        if (sibling.matches('a')) this.ctaLink = sibling;
        sibling = sibling.nextElementSibling;
      }
      if (this.ctaLink) {
        // Cache the authored default so an option with no CTA link restores it.
        this.defaultCtaHref = this.ctaLink.getAttribute('href') || '';
        // Sync the button with the initially active option (hidden when that
        // option has no CTA link of its own).
        this.updateCtaLink(this.dataset.active);
      }
    }

    this.pills.forEach((pill) => {
      pill.addEventListener('click', this.handleClick);
      pill.addEventListener('keydown', this.handleKeyDown);
    });
  }

  disconnectedCallback() {
    this.pills.forEach((pill) => {
      pill.removeEventListener('click', this.handleClick);
      pill.removeEventListener('keydown', this.handleKeyDown);
    });
  }

  handleClick = (event) => {
    const option = event.currentTarget.dataset.option;
    if (option) this.activate(option);
  };

  handleKeyDown = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const currentIndex = this.pills.indexOf(event.currentTarget);
    if (currentIndex === -1) return;
    const delta = event.key === 'ArrowRight' ? 1 : -1;
    const nextIndex = (currentIndex + delta + this.pills.length) % this.pills.length;
    const nextPill = this.pills[nextIndex];
    if (!nextPill) return;
    this.activate(nextPill.dataset.option);
    nextPill.focus();
  };

  activate(option) {
    if (!option) return;
    this.dataset.active = option;
    this.pills.forEach((pill) => {
      const isActive = pill.dataset.option === option;
      pill.setAttribute('aria-selected', String(isActive));
      pill.setAttribute('tabindex', isActive ? '0' : '-1');
    });
    this.panels.forEach((panel) => {
      if (panel.dataset.option === option) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });
    this.updatePanelImage(option);
    this.updateCtaLink(option);
  }

  /**
   * Point the sibling CTA button at the active option's URL. When the option
   * has no authored CTA link, hide the button altogether so a store without a
   * booking page never sends visitors to another store's link. No-op when
   * there is no CTA to update.
   *
   * @param {string} option - "option-1" | "option-2" | "option-3"
   */
  updateCtaLink(option) {
    if (!this.ctaLink) return;

    const n = this.optionIndex(option);
    const href = this.dataset[`option${n}CtaLink`];
    if (href) {
      this.ctaLink.setAttribute('href', href);
      this.ctaLink.removeAttribute('hidden');
    } else {
      this.ctaLink.setAttribute('href', this.defaultCtaHref);
      this.ctaLink.setAttribute('hidden', '');
    }
  }

  /**
   * Swap the sibling tab-panel image to the active option's image. When the
   * option has no authored image (or the block exposes none), restore the
   * panel's default image. No-op when there is no panel image to update.
   *
   * @param {string} option - "option-1" | "option-2" | "option-3"
   */
  updatePanelImage(option) {
    if (!this.panelImage) return;

    const n = this.optionIndex(option);
    const src = this.dataset[`option${n}ImageSrc`];
    const srcset = this.dataset[`option${n}ImageSrcset`];

    const nextSrc = src || this.defaultImageSrc;
    const nextSrcset = srcset || this.defaultImageSrcset;

    if (nextSrc) this.panelImage.setAttribute('src', nextSrc);
    if (nextSrcset) {
      this.panelImage.setAttribute('srcset', nextSrcset);
    } else {
      this.panelImage.removeAttribute('srcset');
    }
  }

  /**
   * Preload any authored option images so switching pills doesn't show a blank
   * frame while the new image downloads. Best-effort and side-effect free —
   * decoded images sit in the browser cache and are otherwise unreferenced.
   */
  /**
   * Map an option token to the numeric suffix used by the data attributes
   * (`data-option<N>-image-src`, `data-option<N>-cta-link`).
   *
   * @param {string} option - "option-1" | "option-2" | "option-3"
   * @returns {string} "1" | "2" | "3"
   */
  optionIndex(option) {
    const match = /^option-([1-3])$/.exec(option || '');
    return match ? match[1] : '1';
  }

  preloadOptionImages() {
    for (const src of [
      this.dataset.option1ImageSrc,
      this.dataset.option2ImageSrc,
      this.dataset.option3ImageSrc,
    ]) {
      if (src) {
        const preloader = new Image();
        preloader.src = src;
      }
    }
  }
}

if (!customElements.get('pill-toggle')) {
  customElements.define('pill-toggle', PillToggle);
}
